<template>
  <DefaultAuthLayout>
    <!-- Top Action Slot -->
    <template #topAction>
      <span class="text-gray mr-3 lg:inline">{{ t('views.login.label.no_account') }}</span>
      <router-link to="cadastro" class="border border-stroke px-3 py-1 text-sm text-body transition-colors hover:bg-gray-2 lg:px-6 lg:py-2 lg:text-base">
        {{ t('views.login.action.create_account') }}
      </router-link>
    </template>

    <!-- Header Slot -->
    <template #header>
      <h2 class="mb-2 text-2xl font-bold text-black lg:text-3xl">{{ t('views.login.title') }}</h2>
      <p class="text-sm text-body lg:text-base">Insira seus dados de acesso</p>
    </template>

    <!-- Form Slot -->
    <template #form>
      <form @submit.prevent="handleSubmit" class="space-y-4 lg:space-y-6">
        <!-- Email Field -->
        <div class="mb-3 lg:mb-4">
          <label class="mb-2 block text-xs font-medium uppercase tracking-wide text-black lg:text-sm">
            {{ t('views.login.label.email') }}
          </label>
          <div class="relative">
            <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 lg:pl-4">
              <IcEmail />
            </div>
            <input
              type="email"
              name="email"
              v-model="form.email"
              class="w-full border border-stroke bg-transparent py-2.5 pl-10 pr-3 text-sm text-black outline-none transition-colors focus:border-blue focus:ring-2 focus:ring-blue lg:py-3 lg:pl-12 lg:pr-4 lg:text-base" />
          </div>
        </div>

        <!-- Password Field -->
        <div class="mb-3 lg:mb-4">
          <div class="mb-2 flex items-center justify-between">
            <label class="block text-xs font-medium uppercase tracking-wide text-black lg:text-sm">
              {{ t('views.login.label.password') }}
            </label>
            <router-link to="forgot-password" class="text-xs font-medium text-blue-2 hover:text-blue lg:text-sm">
              {{ t('views.login.action.forgot_password') }}
            </router-link>
          </div>
          <div class="relative">
            <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 lg:pl-4">
              <IcLock />
            </div>
            <input
              :type="showPassword ? 'text' : 'password'"
              name="password"
              v-model="form.password"
              class="w-full border border-stroke bg-transparent py-2.5 pl-10 pr-10 text-sm text-black outline-none transition-colors focus:border-blue focus:ring-2 focus:ring-blue lg:py-3 lg:pl-12 lg:pr-12 lg:text-base" />
            <button type="button" @click="showPassword = !showPassword" class="absolute inset-y-0 right-0 flex items-center pr-3 lg:pr-4">
              <IcEyeOff v-if="showPassword" />
              <IcEye v-else />
            </button>
          </div>
        </div>

        <!-- Remember Me -->
        <div class="mb-4 flex items-center lg:mb-6">
          <input
            type="checkbox"
            id="remember"
            v-model="rememberMe"
            class="h-3 w-3 border-stroke bg-gray-100 text-blue focus:ring-2 focus:ring-blue lg:h-4 lg:w-4" />
          <label for="remember" class="ml-2 cursor-pointer text-xs text-body lg:text-sm"> Lembrar-me </label>
        </div>

        <!-- Sign In Button -->
        <div class="mb-3 mt-4 lg:mb-5 lg:mt-6">
          <button
            @click="handleSubmit"
            :disabled="isLoading"
            class="flex w-full cursor-pointer justify-center border border-primary bg-primary p-3 font-medium text-white transition hover:bg-blue disabled:cursor-not-allowed disabled:opacity-50 lg:p-4">
            <div class="flex flex-row items-center text-center">
              <div v-if="isLoading" class="mr-3 animate-spin items-center">
                <svg class="h-5 w-5 text-white lg:h-7 lg:w-7" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <span class="flex items-center justify-center text-base font-medium lg:text-lg">
                {{ isLoading ? 'Verificando...' : 'Entrar' }}
              </span>
            </div>
          </button>
        </div>
      </form>
    </template>
  </DefaultAuthLayout>
</template>

<script setup lang="ts">
import DefaultAuthLayout from '@/components/Auths/DefaultAuthLayout.vue'
import { IcEmail, IcEye, IcEyeOff, IcLock } from '@/components/Icons'
import { useI18n } from '@/composable/useI18n'
import { ILoginParams } from '@/interfaces/api/user'
import { useErrorStore } from '@/store/error'
import { useUserStore } from '@/store/user'
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import * as yup from 'yup'

const { t } = useI18n()
const router = useRouter()
const { login } = useUserStore()
const isLoading = ref(false)
const errorStore = useErrorStore()
const showPassword = ref(false)
const rememberMe = ref(false)

const form = ref({
  email: '',
  password: '',
})

const schema = yup.object({
  email: yup
    .string()
    .required(t('errors.required', { label: t('views.login.label.email') }))
    .email(t('errors.invalid', { label: t('views.login.label.email') }))
    .label(t('views.login.labels.email')),
  password: yup
    .string()
    .required(t('errors.required', { label: t('views.login.label.password') }))
    .label(t('views.login.label.password')),
})

function handleSubmit() {
  schema
    .validate(form.value)
    .then(() => {
      doLogin(form.value)
    })
    .catch((err) => {
      errorStore.setErrors(err.errors)
    })
}

const doLogin = (values: ILoginParams) => {
  isLoading.value = true

  login(values, {
    onSuccess: () => {
      router.push({ name: 'home' })
    },
    onFailure: () => {
      router.push({ name: 'account-activation' })
    },
    // Login com senha temporaria — abre tela de redefinicao em vez do app.
    // A senha temporaria ja foi guardada no store pela action login().
    onRequiresPasswordChange: () => {
      router.push({ name: 'update-password' })
    },
    onCompletion: () => {
      isLoading.value = false
    },
  })
}
</script>
